import { createTheme } from '@mui/material/styles';

/**
 * K-Pay Design System
 * Unified design tokens matching the customer portal for brand consistency
 * Based on 8px spacing grid and ratio-based typography scale
 */

// K-Pay Professional Color Palette
const colors = {
  // K-Pay Brand Colors
  brand: {
    kDark: '#111827',     // K- dark color (gray-900)
    payBlue: '#3b82f6',   // Pay blue color (primary blue 500)
    primary: '#3b82f6',   // Primary blue
    secondary: '#2563eb', // Secondary blue (darker)
    kenyaRed: '#EB1626',  // Kenya flag red accent
  },
  // Primary - Blue (Main brand color for CTAs, navigation, emphasis)
  primary: {
    main: '#3b82f6',      // Blue 500 - Primary brand color
    light: '#60a5fa',     // Blue 400 - Lighter variant
    dark: '#2563eb',      // Blue 600 - Darker variant / hover states
    contrastText: '#ffffff',
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  // Secondary - Darker Blue (Supporting actions, complementary elements)
  secondary: {
    main: '#2563eb',      // Blue 600
    light: '#3b82f6',     // Blue 500
    dark: '#1d4ed8',      // Blue 700
    contrastText: '#ffffff',
  },
  // Success - Green (Confirmations, positive states)
  success: {
    main: '#10b981',      // Green 500 - Success states
    light: '#34d399',     // Green 400 - Lighter variant
    dark: '#059669',      // Green 600 - Darker variant
    contrastText: '#ffffff',
    bg: '#d1fae5',        // Green 100 - Light background
  },
  // Warning - Orange (Caution, pending states)
  warning: {
    main: '#f59e0b',      // Orange 500 - Warning states
    light: '#fbbf24',     // Orange 400 - Lighter variant
    dark: '#d97706',      // Orange 600 - Darker variant
    contrastText: '#ffffff',
    bg: '#fed7aa',        // Orange 200 - Light background
  },
  // Error/Accent - Kenya Red (Destructive actions, errors, alerts)
  error: {
    main: '#EB1626',      // Kenya Red - Alert/Accent color
    light: '#f87171',     // Red 400 - Lighter variant
    dark: '#dc2626',      // Red 600 - Darker variant
    contrastText: '#ffffff',
    bg: '#fee2e2',        // Red 100 - Light background
  },
  // Info - Cyan (Informational messages, secondary emphasis)
  info: {
    main: '#06b6d4',      // Cyan 500 - Info states
    light: '#22d3ee',     // Cyan 400 - Lighter variant
    dark: '#0891b2',      // Cyan 600 - Darker variant
    contrastText: '#ffffff',
    bg: '#cffafe',        // Cyan 100 - Light background
  },
  // Neutral - Gray scale (Text, borders, backgrounds)
  grey: {
    50: '#f9fafb',        // Subtle backgrounds
    100: '#f3f4f6',       // Light backgrounds
    200: '#e5e7eb',       // Dividers
    300: '#d1d5db',       // Borders
    400: '#9ca3af',       // Disabled text
    500: '#6b7280',       // Placeholder text
    600: '#4b5563',       // Secondary text
    700: '#374151',       // Body text
    800: '#1f2937',       // Emphasized text
    900: '#111827',       // Headings
  },
  background: {
    default: '#f9fafb',   // Main background (gray-50)
    paper: '#ffffff',     // Card/paper background
    light: '#f3f4f6',     // Alternate light background (gray-100)
  },
  // Gradients (135deg angle for consistency)
  gradients: {
    primary: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    primaryHover: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    primarySoft: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
    success: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    danger: 'linear-gradient(135deg, #EB1626 0%, #dc2626 100%)',
    warning: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    info: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    hero: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 197, 253, 0.05) 100%)',
    card: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
    cardHover: 'linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.95) 100%)',
    overlay: 'linear-gradient(135deg, rgba(59, 130, 246, 0.03) 0%, rgba(147, 197, 253, 0.03) 100%)',
    overlayStrong: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(147, 197, 253, 0.08) 100%)',
  },
  // Shadows - matching customer portal
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    button: '0 4px 14px rgba(59, 130, 246, 0.25)',
    buttonHover: '0 6px 20px rgba(59, 130, 246, 0.35)',
    vibrant: '0 8px 32px rgba(59, 130, 246, 0.12)',
    vibrantLg: '0 20px 60px rgba(59, 130, 246, 0.15)',
    vibrantHover: '0 12px 40px rgba(59, 130, 246, 0.18)',
  },
};

// Typography System - Plus Jakarta Sans
const typography = {
  fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",

  // Font sizes (ratio-based scale)
  fontSize: {
    xs: '0.75rem',    // 12px - Captions, small labels
    sm: '0.8125rem',  // 13px - Small text, badges
    base: '0.875rem', // 14px - Body text
    md: '0.9375rem',  // 15px - Emphasized body
    lg: '1rem',       // 16px - Large body, button text
    xl: '1.125rem',   // 18px - Card titles, section subtitles
    '2xl': '1.25rem', // 20px - Section titles
    '3xl': '1.5rem',  // 24px - Page titles
    '4xl': '2rem',    // 32px - Hero titles
    '5xl': '2.5rem',  // 40px - Large hero text
    '6xl': '3rem',    // 48px - Extra large hero
  },

  // Line heights
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },

  // Font weights
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },

  // Letter spacing
  letterSpacing: {
    tighter: '-0.02em',
    tight: '-0.01em',
    normal: '0em',
    wide: '0.01em',
    wider: '0.05em',
  },
};

// Spacing System - 8px base grid
const spacing = {
  xs: 4,    // 4px
  sm: 8,    // 8px
  md: 12,   // 12px
  lg: 16,   // 16px
  xl: 20,   // 20px
  '2xl': 24,  // 24px
  '3xl': 32,  // 32px
  '4xl': 40,  // 40px
  '5xl': 48,  // 48px
  '6xl': 64,  // 64px
};

// Border Radius System
const radius = {
  sm: 8,    // 8px - Icon containers
  md: 12,   // 12px - Buttons, Inputs
  lg: 16,   // 16px - Cards
  xl: 20,   // 20px - Large cards
  '2xl': 24,  // 24px - Extra large cards
  full: 9999, // Full rounded (pills, badges)
};

// Component Sizing Standards
const components = {
  button: {
    height: {
      sm: 36,   // 36px
      md: 44,   // 44px
      lg: 56,   // 56px
    },
    padding: {
      sm: '8px 16px',
      md: '12px 24px',
      lg: '16px 32px',
    },
  },
  input: {
    height: {
      sm: 40,   // 40px
      md: 48,   // 48px
      lg: 56,   // 56px
    },
  },
  card: {
    padding: {
      sm: 16,   // 16px
      md: 24,   // 24px
      lg: 32,   // 32px
    },
  },
  icon: {
    size: {
      xs: 16,   // 16px
      sm: 20,   // 20px
      md: 24,   // 24px
      lg: 32,   // 32px
      xl: 40,   // 40px
    },
  },
};

// Transitions & Animations
const transitions = {
  fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
  base: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
};

// Z-index Scale
const zIndex = {
  base: 1,
  dropdown: 10,
  sticky: 20,
  fixed: 30,
  modalBackdrop: 40,
  modal: 50,
  popover: 60,
  tooltip: 70,
};

// Create the theme
export const theme = createTheme({
  palette: {
    primary: colors.primary,
    secondary: colors.secondary,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
    background: colors.background,
    grey: colors.grey,
    text: {
      primary: colors.grey[900],
      secondary: colors.grey[600],
      disabled: colors.grey[400],
    },
  },

  typography: {
    fontFamily: typography.fontFamily,
    h1: {
      fontSize: typography.fontSize['5xl'],    // 40px
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.lineHeight.tight,
      letterSpacing: typography.letterSpacing.tighter,
    },
    h2: {
      fontSize: typography.fontSize['4xl'],    // 32px
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.lineHeight.tight,
      letterSpacing: typography.letterSpacing.tight,
    },
    h3: {
      fontSize: typography.fontSize['3xl'],    // 24px
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.tight,
      letterSpacing: typography.letterSpacing.tight,
    },
    h4: {
      fontSize: typography.fontSize['2xl'],    // 20px
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.normal,
    },
    h5: {
      fontSize: typography.fontSize.xl,        // 18px
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.normal,
    },
    h6: {
      fontSize: typography.fontSize.lg,        // 16px
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.normal,
    },
    subtitle1: {
      fontSize: typography.fontSize.lg,        // 16px
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.normal,
    },
    subtitle2: {
      fontSize: typography.fontSize.base,      // 14px
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.normal,
    },
    body1: {
      fontSize: typography.fontSize.base,      // 14px
      fontWeight: typography.fontWeight.regular,
      lineHeight: typography.lineHeight.normal,
    },
    body2: {
      fontSize: typography.fontSize.sm,        // 13px
      fontWeight: typography.fontWeight.regular,
      lineHeight: typography.lineHeight.normal,
    },
    button: {
      textTransform: 'none',
      fontWeight: typography.fontWeight.semibold,
      fontSize: typography.fontSize.base,      // 14px
    },
    caption: {
      fontSize: typography.fontSize.xs,        // 12px
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.normal,
      color: colors.grey[600],
    },
  },

  spacing: 8,  // 8px base unit

  shape: {
    borderRadius: radius.md,  // 12px default
  },

  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.05)',
    '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
    '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  ],

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: `${radius.md}px`,
          fontWeight: typography.fontWeight.semibold,
          fontSize: typography.fontSize.base,
          transition: transitions.base,
          boxShadow: 'none',
        },
        sizeSmall: {
          height: `${components.button.height.sm}px`,
          padding: components.button.padding.sm,
          fontSize: typography.fontSize.sm,
        },
        sizeMedium: {
          height: `${components.button.height.md}px`,
          padding: components.button.padding.md,
        },
        sizeLarge: {
          height: `${components.button.height.lg}px`,
          padding: components.button.padding.lg,
          fontSize: typography.fontSize.lg,
        },
        contained: {
          boxShadow: colors.shadows.sm,
          '&:hover': {
            boxShadow: colors.shadows.md,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: colors.shadows.sm,
          },
        },
        containedPrimary: {
          background: colors.gradients.primary,
          color: '#ffffff',
          boxShadow: colors.shadows.button,
          '&:hover': {
            background: colors.gradients.primaryHover,
            boxShadow: colors.shadows.buttonHover,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            background: colors.gradients.primaryHover,
            transform: 'translateY(0)',
            boxShadow: colors.shadows.button,
          },
          '&:disabled': {
            background: 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)',
            color: 'rgba(255, 255, 255, 0.7)',
            boxShadow: 'none',
            opacity: 0.5,
          },
        },
        containedSuccess: {
          background: colors.gradients.success,
          color: '#ffffff',
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.success.dark} 0%, ${colors.success.dark} 100%)`,
            color: '#ffffff',
          },
          '&:disabled': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
        },
        containedError: {
          background: colors.gradients.danger,
          color: '#ffffff',
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.error.dark} 0%, ${colors.error.dark} 100%)`,
            color: '#ffffff',
          },
          '&:disabled': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
        },
        containedWarning: {
          background: colors.gradients.warning,
          color: '#ffffff',
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.warning.dark} 0%, ${colors.warning.dark} 100%)`,
            color: '#ffffff',
          },
          '&:disabled': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
        },
        containedInfo: {
          background: colors.gradients.info,
          color: '#ffffff',
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.info.dark} 0%, ${colors.info.dark} 100%)`,
            color: '#ffffff',
          },
          '&:disabled': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: `${radius.lg}px`,
          boxShadow: colors.shadows.base,
          transition: `transform ${transitions.base}, box-shadow ${transitions.base}`,
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation0: {
          boxShadow: 'none',
        },
        elevation1: {
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: colors.grey[50],
            color: colors.grey[700],
            fontWeight: 600,
            fontSize: '0.813rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: `2px solid ${colors.grey[200]}`,
          },
        },
      },
    },

    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            '&:nth-of-type(even)': {
              backgroundColor: colors.grey[50],
            },
            '&:hover': {
              backgroundColor: `${colors.primary[50]} !important`,
              transition: 'background-color 0.2s ease',
            },
          },
          '& .MuiTableCell-root': {
            borderBottom: `1px solid ${colors.grey[200]}`,
            padding: '16px',
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '16px',
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.75rem',
        },
        colorSuccess: {
          backgroundColor: colors.success.bg,
          color: colors.success.dark,
        },
        colorWarning: {
          backgroundColor: colors.warning.bg,
          color: colors.warning.dark,
        },
        colorError: {
          backgroundColor: colors.error.bg,
          color: colors.error.dark,
        },
        colorInfo: {
          backgroundColor: colors.info.bg,
          color: colors.info.dark,
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: `${radius.md}px`,
            transition: transitions.base,
            '&:hover fieldset': {
              borderColor: colors.primary[300],
            },
            '&.Mui-focused fieldset': {
              borderWidth: '2px',
            },
          },
          '& .MuiInputBase-input': {
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.regular,
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        outlined: {
          borderRadius: `${radius.md}px`,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: `${radius.xl}px`,
          boxShadow: colors.shadows.xl,
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: `${radius.md}px`,
          transition: transitions.base,
          '&.Mui-selected': {
            backgroundColor: `${colors.primary[50]}`,
            '&:hover': {
              backgroundColor: `${colors.primary[100]}`,
            },
          },
          '&:hover': {
            transform: 'translateX(4px)',
          },
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: typography.fontWeight.semibold,
          fontSize: typography.fontSize.xs,
          borderRadius: `${radius.full}px`,
        },
      },
    },

    MuiAlert: {
      defaultProps: {
        variant: 'filled',
      },
      styleOverrides: {
        root: {
          borderRadius: `${radius.md}px`,
          fontWeight: typography.fontWeight.medium,
          fontSize: typography.fontSize.base,
          boxShadow: colors.shadows.md,
          alignItems: 'center',
          '& .MuiAlert-icon': {
            color: '#ffffff',
            opacity: 1,
          },
          '& .MuiAlert-action': {
            color: '#ffffff',
            paddingTop: 0,
          },
          '& .MuiAlert-message': {
            // Filled alerts have a colored background; force all descendant text
            // (including Typography variants with their own color overrides) to inherit white.
            color: '#ffffff',
            '& .MuiTypography-root': {
              color: 'inherit',
            },
          },
        },
        filledSuccess: {
          backgroundColor: colors.success.main,
          color: '#ffffff',
        },
        filledError: {
          backgroundColor: colors.error.main,
          color: '#ffffff',
        },
        filledWarning: {
          backgroundColor: colors.warning.main,
          color: '#ffffff',
        },
        filledInfo: {
          backgroundColor: colors.info.main,
          color: '#ffffff',
        },
      },
    },
  },
});

// Export all design tokens for use in components
export {
  colors,
  typography as typographyTokens,
  spacing as spacingTokens,
  radius,
  components as componentSizes,
  transitions,
  zIndex
};

export default theme;
